import React from 'react'
import NextImage from 'next/image'
import { useRouter } from 'next/router'

import { Button } from '@wartek-id/button-v1'
import {
  Dialog,
  DialogContent,
  DialogCloseButton,
  DialogOverlay,
} from '@wartek-id/dialog-v1'
import { Text } from '@wartek-id/text-v1'

import Stepper from 'components/Stepper'
import { SimpleCard } from 'app/KinerjaPSPegawaiPage/components/SimpleCard'
import PelaksanaanPraktikKinerjaCardPegawai from 'app/KinerjaPSPegawaiPage/components/PelaksanaanPraktikKinerjaCard'
import PelaksanaanPraktikKinerjaCardPenilai from 'app/KinerjaTimPenilaiPSPage/components/PelaksanaanPraktikKinerjaCard'
import { PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import useBasePath from 'app/KinerjaTimPenilaiPSPage/hooks/useBasePath'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'

import type {
  StatusPelaksanaanPraktikKinerja,
  RhkPs,
  StatusPelaksanaanPraktikKinerjaMetadata,
} from 'types/kinerja-ps-pegawai'

const STEPS = [
  {
    title: 'Diskusi Persiapan',
    imageSrc: '/illustrations/img-document-penci-fill.png',
    path: 'diskusi-persiapan',
  },
  {
    title: 'Pendampingan Satuan Pendidikan',
    imageSrc: '/illustrations/img-assessment-clipboard-school.png',
    path: 'pendampingan-satuan-pendidikan',
  },
  {
    title: 'Diskusi Tindak Lanjut',
    imageSrc: '/illustrations/img-document-interaction-discussion.png',
    path: 'diskusi-tindak-lanjut',
  },
  {
    title: 'Upaya Tindak Lanjut',
    imageSrc: '/illustrations/img-clip-board-summary.png',
    path: 'upaya-tindak-lanjut',
  },
  {
    title: 'Refleksi Tindak Lanjut',
    imageSrc: '/illustrations/img-disiplin-positif-stundet-board.png',
    path: 'refleksi-tindak-lanjut',
  },
]

type TDialogPraktikKinerjaSelectionProps = {
  praktikKinerjaRhkPs: RhkPs
  isOpen: boolean
  onClose: () => void
}
const DialogPraktikKinerjaSelection: React.FC<
  TDialogPraktikKinerjaSelectionProps
> = ({ praktikKinerjaRhkPs, isOpen, onClose }) => {
  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      autoFocus={false}
      returnFocus={false}
      size="sm"
    >
      <DialogOverlay />
      <DialogContent>
        <div
          data-testid="selected-praktik-kinerja-dialog"
          className="flex p-6 space-x-6"
        >
          <div>
            <NextImage
              className="w-16 h-16"
              src="https://static.guru.belajar.id/kinerja-ps/img-praktik-kinerja-1.png"
              height="64px"
              width="64px"
            />
          </div>

          <div className="space-y-2">
            <Text variant="heading-sm">{praktikKinerjaRhkPs?.nama}</Text>
            <Text variant="body-sm" color="subdued">
              <div
                dangerouslySetInnerHTML={{
                  __html: (praktikKinerjaRhkPs?.keterangan ?? '').replace(
                    /\\n/g,
                    '<br />'
                  ),
                }}
              />
            </Text>
          </div>
        </div>
        <DialogCloseButton />
      </DialogContent>
    </Dialog>
  )
}

type TPelaksanaanPraktikKinerjaStagesProps = {
  currentState: StatusPelaksanaanPraktikKinerja
  appealState?: StatusPelaksanaanPraktikKinerja
  currentStateMeta?: StatusPelaksanaanPraktikKinerjaMetadata
  leftSectionTitle?: string
  rightSectionTitle?: string
  praktikKinerjaRhkPs: RhkPs
  stages: StatusPelaksanaanPraktikKinerja[][]
}
const PelaksanaanPraktikKinerjaStages: React.FC<
  TPelaksanaanPraktikKinerjaStagesProps
> = ({
  currentState,
  appealState,
  currentStateMeta,
  leftSectionTitle = '',
  rightSectionTitle = '',
  praktikKinerjaRhkPs,
  stages,
}) => {
  const router = useRouter()
  const skpPsId = router?.query?.skpPsId as string
  const [isDialogOpen, setDialogOpen] = React.useState<boolean>(false)

  const currentStep = stages.findIndex((stage) => stage.includes(currentState))
  const appealStep = stages.findIndex((stage) => stage.includes(appealState))
  const { basePath, isTimKinerjaPengawasSekolahUrl, isPPKPengawasSekolahUrl } =
    useBasePath()
  const isTimPenilai = isTimKinerjaPengawasSekolahUrl || isPPKPengawasSekolahUrl
  const { isPegawaiPengawasSekolah } = useRoleContext()

  const PelaksanaanPraktikKinerjaCard = isTimPenilai
    ? PelaksanaanPraktikKinerjaCardPenilai
    : PelaksanaanPraktikKinerjaCardPegawai

  const getRedirectPath = (path: string): string => {
    return isTimPenilai
      ? `${basePath}/penilai/pelaksanaan/${skpPsId}/praktik-kinerja/${path}`
      : `${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/pelaksanaan/praktik-kinerja/${path}`
  }

  const isRefleksiTindakLanjutDisabled = React.useCallback(
    (index) => {
      return (
        isTimPenilai &&
        currentState === 'REFLEKSI_TINDAK_LANJUT' &&
        stages[index].includes('REFLEKSI_TINDAK_LANJUT')
      )
    },
    [currentState, isTimPenilai, stages]
  )

  return (
    <div>
      <div className="grid grid-cols-12 gap-x-6">
        <div className="col-span-9">
          <div className="space-y-3">
            <Text variant="heading-sm">{leftSectionTitle}</Text>
            <Stepper
              currentStep={currentStep}
              steps={STEPS.map(({ title, imageSrc, path }, index) => {
                let isDisabled =
                  index > currentStep || isRefleksiTindakLanjutDisabled(index)
                const isDone = index < currentStep
                const isAppeal = Boolean(appealState) && index >= appealStep

                // disable when "Upaya Tindak Lanjut" or "Refleksi Tindak Lanjut" not filled yet
                if (
                  isPegawaiPengawasSekolah &&
                  !isDone &&
                  isAppeal &&
                  [3, 4].includes(index)
                ) {
                  isDisabled = true
                }

                return {
                  isDisabled,
                  isDone: !isAppeal && isDone,
                  element: (
                    <PelaksanaanPraktikKinerjaCard
                      key={`praktik-kinerja-${index}-card`}
                      data-testid={`praktik-kinerja${
                        isDisabled ? '-disabled' : ''
                      }-card`}
                      imageSrc={imageSrc}
                      title={title}
                      href={getRedirectPath(path)}
                      currentState={currentState}
                      currentStateMeta={currentStateMeta}
                      states={
                        stages[index] as StatusPelaksanaanPraktikKinerja[]
                      }
                      disabled={isDisabled}
                      done={isDone}
                      appeal={isAppeal}
                    />
                  ),
                }
              })}
            />
          </div>
        </div>
        <div className="col-span-3">
          <div className="space-y-3">
            <Text variant="heading-sm">{rightSectionTitle}</Text>
            <SimpleCard className="p-4 space-y-3">
              <div>
                <NextImage
                  className="w-9 h-9"
                  src="https://static.guru.belajar.id/kinerja-ps/img-praktik-kinerja-1.png"
                  height="36px"
                  width="36px"
                />
              </div>
              <Text variant="heading-sm">{praktikKinerjaRhkPs?.nama}</Text>
              <Button
                variant="ghost"
                className="!px-0"
                onClick={() => {
                  setDialogOpen(true)
                }}
              >
                Selengkapnya
              </Button>
            </SimpleCard>
          </div>
        </div>
      </div>

      <DialogPraktikKinerjaSelection
        praktikKinerjaRhkPs={praktikKinerjaRhkPs}
        isOpen={isDialogOpen}
        onClose={() => {
          setDialogOpen(false)
        }}
      />
    </div>
  )
}

export default PelaksanaanPraktikKinerjaStages
